import React, { Fragment } from 'react';
import {
  ChipField,
  Datagrid,
  Filter,
  ReferenceField,
  TextField,
  TextInput,
  SelectInput,
  TopToolbar,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { List } from '~/layout';
import {
  NoPermissions,
  StatusField,
  CurrencyAutocomplete,
  BankAccountSelect,
} from '~/components/ra';
import PnlAccountCreate from './PnlAccountCreate';
import { RunTransferAction, UpdateContext } from '~/components';
import { useTenant } from '~/hooks/tenant';
import { statusLabel } from '~/components/StatusField';
import { makeStyles } from '@material-ui/core/styles';

export const l = (key: string): string => `resources.tenant/accounts.${key}`;

const PnlAccountListActions = (props: any) => {
  const { permissions } = usePermissions();
  const { resource } = props;

  const { data: tenant } = useTenant();

  if (!permissions || !tenant) return null;

  // const hasSettlementPermission = permissions.includes('settlement.runTransfer');

  const hasRunTransferPermission =
    permissions.includes('settlement.runTransfer') ||
    permissions.includes('accountTransfer.runTransfer');

  return (
    <TopToolbar>
      {hasRunTransferPermission && (
        <RunTransferAction
          profileId={tenant.id}
          resource={resource}
          onDone={() => {
            // refetch();
          }}
          permissions={permissions}
        />
      )}
      <PnlAccountCreate permissions={permissions} resource={resource} />
    </TopToolbar>
  );
};

const PnlAccountList = (props: any) => {
  const { permissions } = usePermissions();
  const { data: tenant } = useTenant();
  const classes = useStyles();
  const translate = useTranslate();
  const td = (key: string): string => translate(`utils.${key}`);
  const statusChoices = [
    { id: 'ACTIVE', name: translate(statusLabel('ACTIVE')) },
    { id: 'BLOCKED', name: translate(statusLabel('BLOCKED')) },
    { id: 'CLOSED', name: translate(statusLabel('CLOSED')) },
  ];

  if (!permissions) return null;
  if (!permissions?.includes('tenant.account.list')) return <NoPermissions />;
  return (
    <Fragment>
      <UpdateContext data={{ solutionId: tenant?.id }} />
      <List
        bulkActionButtons={false}
        {...props}
        actions={<PnlAccountListActions {...props} />}
        filters={
          <Filter className={classes.form}>
            <TextInput
              label={l('accountNumber')}
              source='number'
              alwaysOn
              className={classes.formControl}
            />
            <SelectInput
              label={l('status')}
              source='status'
              choices={statusChoices}
              alwaysOn
              allowEmpty
              emptyText={td('any')}
              className={classes.formControl}
            />
            <CurrencyAutocomplete
              label={l('currency')}
              source='currency'
              alwaysOn
              className={classes.formControl}
            />
            <SelectInput
              label={l('type')}
              source='type'
              choices={[
                { id: 'REGULAR', name: 'Regular' },
                { id: 'SETTLEMENT', name: 'Settlement' },
                { id: 'META', name: 'Meta' },
              ]}
              alwaysOn
              allowEmpty
              emptyText={td('any')}
              className={classes.formControl}
            />
            <BankAccountSelect
              label={l('liquidityBucket')}
              source='bankAccountId'
              className={classes.formControl}
              allowEmpty
              alwaysOn
              emptyText={td('any')}
            />
          </Filter>
        }
      >
        <Datagrid rowClick='show' size='medium' selectedIds={null}>
          <ChipField source='type' defaultValue='REGULAR' />
          <TextField source='alias' label={l('alias')} />
          <TextField source='number' label={l('accountNumber')} sortable={false} />
          <TextField source='currency' label={l('currency')} />
          <ReferenceField
            source='bankAccountId'
            reference='bank/accounts'
            link={false}
            label={l('liquidityBucket')}
            sortable={false}
          >
            <TextField source='alias' />
          </ReferenceField>
          <TextField source='description' label={l('description')} />
          <StatusField source='status' defaultValue='ACTIVE' />
        </Datagrid>
      </List>
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  form: {
    alignItems: 'flex-start',
    '& > div': {
      '& > div:last-child': {
        width: theme.spacing(1),
      },
    },
  },
  formControl: {
    width: 200,
  },
}));

export default PnlAccountList;
